export function hasSentMagicLink(lead) {
  return (lead?.magicLinks || []).some((entry) => Boolean(entry?.sentAt));
}

export function hasApprovedDraft(lead) {
  return (lead?.articleDrafts || []).some((draft) => Boolean(draft?.approvedAt));
}

export function pickLatestDraftBySource(lead, source) {
  const target = String(source || '').toLowerCase();
  const drafts = (lead?.articleDrafts || [])
    .filter((draft) => String(draft?.source || '').toLowerCase() === target)
    .slice()
    .sort((a, b) => {
      const aTs = Date.parse(a?.updatedAt || a?.createdAt || a?.preparedAt || 0);
      const bTs = Date.parse(b?.updatedAt || b?.createdAt || b?.preparedAt || 0);
      return bTs - aTs;
    });
  return drafts[0] || null;
}

export function mergeDraftIntoLead(lead, draft) {
  if (!lead || !draft) return lead;
  const drafts = Array.isArray(lead.articleDrafts) ? lead.articleDrafts : [];
  const exists = drafts.some((item) => String(item?.id || '') === String(draft?.id || ''));
  return {
    ...lead,
    articleDrafts: exists
      ? drafts.map((item) => (String(item?.id || '') === String(draft?.id || '') ? draft : item))
      : [...drafts, draft],
  };
}

// Any stage at or past the connected lane qualifies as "connected-or-beyond"
// for UI purposes. A booked / replying / nurturing lead still has
// conversation history worth seeing on the Intro and Article tabs — the old
// strict stage === 'connected' check hid those tabs the moment a lead moved
// forward, which left post-booking retros with no way to see the intro that
// started the thread. Pre-connected stages (prospect/invited/queued/
// qualified/contacted) stay on the non-connected tab list.
const CONNECTED_OR_BEYOND_STAGES = new Set([
  'connected',
  'sent', 'clicked', 'booked', 'curious', 'warm', 'hot', 'nurturing', 'article_opened', 'replying',
  'engaged', 'meeting', 'won',
  'closed', 'lost',
]);

export function getIntroProposalState(lead) {
  const stage = String(lead?.stage || '').toLowerCase();
  const isConnected = CONNECTED_OR_BEYOND_STAGES.has(stage);
  // "neverMessaged" still means literally no outbound touch — this gates the
  // "propose an intro?" prompt. It stays strict because once a message has
  // been sent, we don't want to auto-propose another one.
  const neverMessaged = Boolean(lead) && !hasSentMagicLink(lead) && !hasApprovedDraft(lead);
  const latestIntroDraft = pickLatestDraftBySource(lead, 'intro-message');
  const introSkipped = Boolean(latestIntroDraft?.skippedAt);
  // Intro tab is visible when the lead is in the fresh-connected state
  // (never messaged) — this is the window where an operator would propose
  // a new intro. For leads that have progressed past the first touch, the
  // intro that was sent is still visible via the Message/Activity tabs.
  const introTabVisible = Boolean(lead) && stage === 'connected' && neverMessaged;
  const articleTabVisible = Boolean(lead) && isConnected;
  const shouldProposeIntro = introTabVisible && !introSkipped;
  return {
    isConnected,
    neverMessaged,
    latestIntroDraft,
    introSkipped,
    introTabVisible,
    articleTabVisible,
    shouldProposeIntro,
  };
}
